import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, fmtTimeRange } from "@/lib/format";

// Center managers move an appointment to a new date/time. Only open slots
// (enhancer availability / center hours minus conflicts & blackouts) are offered.
export default function RescheduleModal({ booking, onClose, onDone, initialDate = "" }) {
  const [date, setDate] = useState(initialDate || booking.date || "");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: slotData, isFetching } = useQuery({
    queryKey: ["resched-slots", booking.service_id, booking.enhancer_id, date],
    queryFn: () => api.get(`/manager/me/open-slots?service_id=${booking.service_id}&date=${date}&enhancer_id=${booking.enhancer_id || ""}`).then((r) => r.data),
    enabled: !!date && !!booking.service_id,
  });
  const slots = slotData?.slots || [];

  const save = async () => {
    if (!date || !time) { toast.error("Pick a date and an open slot."); return; }
    setBusy(true);
    try {
      await api.put(`/manager/appointments/${booking.id}/reschedule`, { date, start_time: time });
      toast.success(`Rescheduled to ${date} at ${time} — the client has been notified.`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="reschedule-modal">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><CalendarClock size={18} className="text-[#144F75]" /> Reschedule</h3>
          <button onClick={onClose} data-testid="reschedule-close" className="text-[#8FA89B] hover:text-[#1A242B]"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#8FA89B] mb-4">
          {booking.service_title || booking.title}{booking.client_name || booking.name ? ` · ${booking.client_name || booking.name}` : ""} — currently {fmtDate(booking.date)} at {fmtTimeRange(booking.start_time)}. The client is notified by email and in-app.
        </p>
        <label className="text-sm font-medium text-[#1A242B] mb-1 block">New date</label>
        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setTime(""); }} data-testid="reschedule-date" className="eyw-input w-full mb-3" />
        <label className="text-sm font-medium text-[#1A242B] mb-1 block">Open slots {slotData?.duration ? `(${slotData.duration} min)` : ""}</label>
        {isFetching ? (
          <p className="text-xs text-[#8FA89B] mb-2"><Loader2 size={12} className="animate-spin inline" /> Checking availability…</p>
        ) : !booking.service_id ? (
          <p className="text-xs text-[#B5794E] mb-2">No service linked to this appointment — slots can't be computed.</p>
        ) : slots.length === 0 ? (
          <p className="text-xs text-[#B5794E] mb-2" data-testid="reschedule-no-slots">No open slots that day — try another date.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-2 max-h-40 overflow-y-auto" data-testid="reschedule-slots">
            {slots.map((s) => (
              <button key={s} onClick={() => setTime(s)} data-testid={`reschedule-slot-${s.replace(":", "")}`}
                className={`rounded-full px-3 py-1.5 text-xs border ${time === s ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy || !time} data-testid="reschedule-save" className="eyw-btn !py-2 text-sm disabled:opacity-50">
            {busy && <Loader2 className="animate-spin" size={14} />} Confirm new time
          </button>
        </div>
      </div>
    </div>
  );
}
